import React, { useState, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import LoginModal from './LoginModal';
import { useNavigate, useLocation } from 'react-router-dom';

const serviceCategories = {
  machineryEquipment: {
    title: "Machinery & Equipment",
    items: [
      { title: "Engines and Casings", path: "/services/engines-casings" },
      { title: "Fans, Blowers and Compressors", path: "/services/fans-blowers" },
      { title: "Gaskets, Seals and Shims", path: "/services/gaskets-seals" },
      { title: "Valves, Pipes and Fittings", path: "/services/pipeline-repair" },
      { title: "Solids Handling Machinery", path: "/services/solids-handling" },
      { title: "Heat Exchangers", path: "/services/hvac-repair" },
      { title: "Mechanical Power Transmission", path: "/services/mechanical-transmission" },
      { title: "Tanks and Secondary Containment", path: "/services/tank-repair" },
      { title: "Grease Interceptors", path: "/services/grease-interceptors" },
      { title: "Ships and Offshore Structures", path: "/services/marine-offshore" },
      { title: "Pumps", path: "/services/equipment-repair" },
    ]
  },
  buildingsStructures: {
    title: "Buildings & Structures",
    items: [
      { title: "Floor Repair & Procoating", path: "/services/floor-repair" },
      { title: "Roof Leak Seal & Repair", path: "/services/roof-repair" },
      { title: "Wall Problem Areas", path: "/services/structural-repair" },
    ]
  },
  ourServices: {
    title: "Our Services",
    items: [
      { title: "Protective Coatings & Corrosion Prevention", path: "/services/protective-coatings" },
      { title: "Chillers, Condensers & Cooling Towers", path: "/services/hvac-repair" },
      { title: "Pipe Repairs & Leak Sealing", path: "/services/pipeline-repair" },
      { title: "Structural Repair & Reinforcement", path: "/services/structural-repair" },
      { title: "Rotating Equipment & Pump Restoration", path: "/services/equipment-repair" },
      { title: "Industrial Cleaning & Media-Blasting", path: "/services/industrial-cleaning" },
      { title: "Cumulative Tank Repair", path: "/services/tank-repair" },
      { title: "Roof Leak Seal & Repair", path: "/services/roof-repair" },
      { title: "Floor Repair & Procoating", path: "/services/floor-repair" },
      { title: "Asset Life Extension Program", path: "/services/asset-life-extension" },
    ]
  }
};

const maintenanceMenuItems = [
  { title: "SMART Program Overview", path: "/maintenance/smart-program" },
  { title: "Systematic Inspections", path: "/maintenance/systematic-inspections" },
  { title: "Material-Specific Solutions", path: "/maintenance/material-specific-solutions" },
  { title: "Active Corrosion Control", path: "/maintenance/active-corrosion-control" },
  { title: "Reliable Repair Methods", path: "/maintenance/reliable-repair-methods" },
  { title: "Total Asset Lifecycle", path: "/maintenance/total-asset-lifecycle" },
];

const industriesMenuItems = [
  { title: "Oil and Gas", path: "/industries/oil-gas" },
  { title: "Steel", path: "/industries/steel" },
  { title: "Marine", path: "/industries/marine" },
  { title: "Power", path: "/industries/power" },
  { title: "Pulp and Paper", path: "/industries/pulp-paper" },
  { title: "Facilities Maintenance", path: "/industries/facilities-maintenance" },
  { title: "Waste to Energy", path: "/industries/waste-to-energy" },
  { title: "Agriculture and Fishing", path: "/industries/agriculture-fishing" },
  { title: "Water and Wastewater", path: "/industries/water-wastewater" },
  { title: "Mining and Quarrying", path: "/industries/mining-quarrying" },
  { title: "General Industry", path: "/industries/general-industry" },
  { title: "Manufacturing", path: "/industries/manufacturing" },
  { title: "Sugar", path: "/industries/sugar" },
  { title: "Education", path: "/industries/education" },
  { title: "Food and Drink", path: "/industries/food-drink" },
  { title: "Petrochemical", path: "/industries/petrochemical" },
  { title: "Pharmaceutical", path: "/industries/pharmaceutical" },
  { title: "Wind Power", path: "/industries/wind-power" },
  { title: "Hydrogen", path: "/industries/hydrogen" },
];

const researchMenuItems = [
  { title: "Blog & Articles", path: "/research-learn" },
  { title: "Corrosion & Rust Issues", path: "/research/corrosion-rust" },
  { title: "Leaks & Seepage", path: "/research/leaks-seepage" },
  { title: "Erosion & Wear", path: "/research/erosion-wear" },
  { title: "Equipment Failures", path: "/research/equipment-failures" },
  { title: "HVAC & Cooling Systems", path: "/research/hvac-cooling" },
  { title: "Structural Damage", path: "/research/structural-damage" },
];

const aboutMenuItems = [
  { title: "Why Choose BCS?", path: "/about/why-choose-bcs" },
  { title: "Mission Statement", path: "/about/mission-statement" },
  { title: "Our Team", path: "/about/our-team" },
  { title: "Our Experience", path: "/about/our-experience" },
  { title: "Customer Testimonials", path: "/about/testimonials" },
  { title: "Associations", path: "/about/associations" },
  { title: "Company Line Card", path: "/marketing-sheet", isLineCard: true },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const dropdownTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleMouseEnter = (menu) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300);
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
      }
    } else {
      navigate('/');
      setMobileMenuOpen(false);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setActiveDropdown(null);
  };

  const goToHome = () => {
    navigate('/');
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleMenuItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
    } else if (item.sectionId) {
      scrollToSection(item.sectionId);
    }
    setActiveDropdown(null);
    setMobileMenuOpen(false);
    setMobileAccordion(null);
  };

  const toggleMobileAccordion = (menu) => {
    setMobileAccordion(mobileAccordion === menu ? null : menu);
  };

  const DropdownMenu = ({ items, isOpen }) => {
    if (!isOpen) return null;
    return (
      <div 
        className="absolute top-full left-0 pt-2 z-50"
        onMouseEnter={() => handleMouseEnter(activeDropdown)}
        onMouseLeave={handleMouseLeave}
      >
        <div className="w-72 bg-white rounded-lg shadow-xl border border-gray-100 py-2">
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => handleMenuItemClick(item)}
              className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors"
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const MobileAccordion = ({ title, items, menuKey }) => (
    <div>
      <button
        onClick={() => toggleMobileAccordion(menuKey)}
        className="text-gray-700 hover:text-blue-900 font-medium text-left flex items-center gap-1 w-full"
      >
        {title}
        <ChevronDown size={16} className={`transition-transform ${mobileAccordion === menuKey ? 'rotate-180' : ''}`} />
      </button>
      {mobileAccordion === menuKey && (
        <div className="pl-4 mt-2 space-y-2 border-l-2 border-blue-100">
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => handleMenuItemClick(item)}
              className="text-gray-600 hover:text-blue-900 text-sm text-left block py-1"
            >
              {item.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
    <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <button
              onClick={goToHome}
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors">
              Home
            </button>
            
            {/* Services Mega Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('services')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-blue-900 font-medium transition-colors flex items-center gap-1 py-2">
                Services
                <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'services' ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Mega Menu */}
              {activeDropdown === 'services' && (
                <div 
                  className="absolute top-full left-0 pt-2 z-50"
                  onMouseEnter={() => handleMouseEnter('services')}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="bg-white rounded-lg shadow-2xl border border-gray-100 p-6 min-w-[700px]">
                    <div className="grid grid-cols-3 gap-6">
                      {/* Machinery & Equipment */}
                      <div>
                        <h3 className="font-bold text-blue-900 bg-blue-100 px-3 py-2 rounded mb-3 text-sm">
                          {serviceCategories.machineryEquipment.title}
                        </h3>
                        <ul className="space-y-1">
                          {serviceCategories.machineryEquipment.items.map((item, index) => (
                            <li key={index}>
                              <button
                                onClick={() => handleMenuItemClick(item)}
                                className="text-sm text-gray-600 hover:text-blue-900 hover:bg-blue-50 w-full text-left px-3 py-1.5 rounded transition-colors"
                              >
                                {item.title}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Buildings & Structures */}
                      <div>
                        <h3 className="font-bold text-blue-900 bg-blue-100 px-3 py-2 rounded mb-3 text-sm">
                          {serviceCategories.buildingsStructures.title}
                        </h3>
                        <ul className="space-y-1">
                          {serviceCategories.buildingsStructures.items.map((item, index) => (
                            <li key={index}>
                              <button
                                onClick={() => handleMenuItemClick(item)}
                                className="text-sm text-gray-600 hover:text-blue-900 hover:bg-blue-50 w-full text-left px-3 py-1.5 rounded transition-colors"
                              >
                                {item.title}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Our Services */}
                      <div>
                        <h3 className="font-bold text-green-800 bg-green-100 px-3 py-2 rounded mb-3 text-sm">
                          {serviceCategories.ourServices.title}
                        </h3>
                        <ul className="space-y-1">
                          {serviceCategories.ourServices.items.map((item, index) => (
                            <li key={index}>
                              <button
                                onClick={() => handleMenuItemClick(item)}
                                className="text-sm text-gray-600 hover:text-green-800 hover:bg-green-50 w-full text-left px-3 py-1.5 rounded transition-colors"
                              >
                                {item.title}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Industries Mega Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('industries')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={() => scrollToSection('industries')}
                className="text-gray-700 hover:text-blue-900 font-medium transition-colors flex items-center gap-1 py-2">
                Industries
                <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'industries' ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Industries Mega Menu */}
              {activeDropdown === 'industries' && (
                <div 
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50"
                  onMouseEnter={() => handleMouseEnter('industries')}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="bg-white rounded-lg shadow-2xl border border-gray-100 p-6 min-w-[650px]">
                    <h3 className="font-bold text-blue-900 bg-blue-100 px-3 py-2 rounded mb-4 text-sm text-center">
                      Industry Areas
                    </h3>
                    <div className="grid grid-cols-3 gap-x-6 gap-y-1">
                      {industriesMenuItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleMenuItemClick(item)}
                          className="text-sm text-gray-600 hover:text-blue-900 hover:bg-blue-50 text-left px-3 py-1.5 rounded transition-colors"
                        >
                          {item.title}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Preventative Maintenance Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('maintenance')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={() => scrollToSection('asset-life-extension')}
                className="text-gray-700 hover:text-blue-900 font-medium transition-colors flex items-center gap-1 py-2">
                Preventative Maintenance
                <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'maintenance' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={maintenanceMenuItems} isOpen={activeDropdown === 'maintenance'} />
            </div>

            <button
              onClick={() => scrollToSection('warranties')}
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors">
              Warranties
            </button>

            {/* Research & Learn Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('research')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={() => scrollToSection('research-learn')}
                className="text-gray-700 hover:text-blue-900 font-medium transition-colors flex items-center gap-1 py-2">
                Research & Learn
                <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'research' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={researchMenuItems} isOpen={activeDropdown === 'research'} />
            </div>

            {/* About Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('about')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={() => scrollToSection('why-choose-bcs')}
                className="text-gray-700 hover:text-blue-900 font-medium transition-colors flex items-center gap-1 py-2">
                About
                <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'about' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={aboutMenuItems} isOpen={activeDropdown === 'about'} />
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors">
              Contact
            </button>

            <button
              onClick={() => navigate('/careers')}
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors">
              Careers
            </button>

            <Button
              onClick={() => setLoginModalOpen(true)}
              className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 hover:bg-blue-800 !font-medium !text-sm gap-2 px-4 py-2 rounded-md shadow text-white bg-blue-900"
            >
              Customer Login
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen &&
        <div className="md:hidden py-4 border-t max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={goToHome}
                className="text-gray-700 hover:text-blue-900 font-medium text-left">
                Home
              </button>
              
              {/* Mobile Services Accordion */}
              <div>
                <button
                  onClick={() => toggleMobileAccordion('services')}
                  className="text-gray-700 hover:text-blue-900 font-medium text-left flex items-center gap-1 w-full"
                >
                  Services
                  <ChevronDown size={16} className={`transition-transform ${mobileAccordion === 'services' ? 'rotate-180' : ''}`} />
                </button>
                {mobileAccordion === 'services' && (
                  <div className="pl-4 mt-2 space-y-3 border-l-2 border-blue-100">
                    <div>
                      <p className="text-xs font-bold text-blue-900 mb-1">{serviceCategories.machineryEquipment.title}</p>
                      {serviceCategories.machineryEquipment.items.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleMenuItemClick(item)}
                          className="text-gray-600 hover:text-blue-900 text-sm text-left block py-1"
                        >
                          {item.title}
                        </button>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-blue-900 mb-1">{serviceCategories.buildingsStructures.title}</p>
                      {serviceCategories.buildingsStructures.items.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleMenuItemClick(item)}
                          className="text-gray-600 hover:text-blue-900 text-sm text-left block py-1"
                        >
                          {item.title}
                        </button>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-green-800 mb-1">{serviceCategories.ourServices.title}</p>
                      {serviceCategories.ourServices.items.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleMenuItemClick(item)}
                          className="text-gray-600 hover:text-green-800 text-sm text-left block py-1"
                        >
                          {item.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <MobileAccordion title="Preventative Maintenance" items={maintenanceMenuItems} menuKey="maintenance" />
              
              <button
                onClick={() => scrollToSection('warranties')}
                className="text-gray-700 hover:text-blue-900 font-medium text-left">
                Warranties
              </button>

              <MobileAccordion title="Industries" items={industriesMenuItems} menuKey="industries" />
              <MobileAccordion title="About" items={aboutMenuItems} menuKey="about" />

              <button
                onClick={() => scrollToSection('contact')}
                className="text-gray-700 hover:text-blue-900 font-medium text-left">
                Contact
              </button>

              <MobileAccordion title="Research & Learn" items={researchMenuItems} menuKey="research" />

              <button
                onClick={() => { navigate('/careers'); setMobileMenuOpen(false); }}
                className="text-gray-700 hover:text-blue-900 font-medium text-left">
                Careers
              </button>

              <Button
                onClick={() => setLoginModalOpen(true)}
                className="bg-blue-900 hover:bg-blue-800 text-white w-full"
              >
                Customer Login
              </Button>
            </nav>
          </div>
        }
      </div>
    </header>
    
    <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </>
  );
};

export default Header;
